import { useState } from "react";
import { Activity } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { sendUserDetails } from "../api";
import bgLogo from "../assets/bg.svg";
import heroImage from "../assets/signal-ai-hero.jpg";


function SignalAIUserDetails() {
  const navigate = useNavigate();
  const location = useLocation();
  const { company, answers, assessment_id } = (location.state || {}) as {
    company?: unknown;
    answers?: unknown;
    assessment_id?: string | number;
  };

  const [form, setForm] = useState({
    name: "",
    email: "",
  });
  const [popup, setPopup] = useState("");

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const isValidEmail = (email: string) =>
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const isValid = form.name.trim().length > 1 && isValidEmail(form.email);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!form.name || !form.email) {
      setPopup("Please fill all required details");
      return;
    }

    try {
      const payload = {
        name: form.name,
        email: form.email,
        assessment_id,
      };

      await sendUserDetails(payload);
      setPopup("OTP sent to your email");

      setTimeout(() => {
        navigate("/signalai-otp-verification", {
          state: {
            user: { name: form.name, email: form.email },
            company,
            answers,
            assessment_id,
          },
        });
      }, 1000);
    } catch (_error) {
      setPopup("Failed to send OTP");
    }
  };

  return (
    <div className="relative min-h-screen w-full flex items-center justify-center bg-gradient-hero px-4 py-10 overflow-hidden">
      <div className="absolute inset-0 bg-gradient-mesh opacity-60 pointer-events-none" aria-hidden />
      <div className="relative w-full max-w-5xl grid grid-cols-1 lg:grid-cols-2 gap-10 items-start">

        {/* Left visual panel */}
        <div className="hidden lg:flex w-full flex-col gap-6">
          <div className="relative w-full rounded-2xl overflow-hidden shadow-elevated border border-border">
            <img
              src={heroImage}
              alt="Signal AI readiness dashboard preview"
              className="w-full h-[420px] object-cover"
            />
            <div className="absolute top-4 left-4 inline-flex items-center gap-2 bg-[#0d1117] px-3 py-1.5 rounded-full text-xs font-semibold text-white shadow-sm border border-white/10">
              <span className="w-2 h-2 rounded-full bg-signal animate-pulse" />
              Signal AI Readiness Report
            </div>
          </div>
          <div className="relative w-full overflow-hidden rounded-2xl border border-slate-800/60 bg-gradient-to-r from-[#020b1f] via-[#0a1833] to-[#111f39] p-7 shadow-elevated">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_80%_10%,rgba(39,148,255,0.18),transparent_45%)]" aria-hidden />
            <p className="relative text-xl font-medium leading-relaxed text-slate-100">
              Every <b>$ you invest in AI</b> should <b>return at least <b>"2x"</b></b>.
               <b>Signal AI</b> shows you where that <b>actually happens.</b>
            </p>
          </div>
        </div>  

        {/* Right form card */}
        <div className="w-full">
          <div className="w-full bg-card border border-border rounded-2xl shadow-elevated p-8 sm:p-10 lg:min-h-[600px]">
        <div className="mb-6 flex w-full items-center justify-between gap-3">
          <div className="flex items-center gap-2.5">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-dark">
              <Activity className="h-4 w-4 text-signal" strokeWidth={2.5} />
            </div>
            <div className="flex items-center gap-2">
              <span className="text-base font-semibold tracking-tight text-foreground">Signal AI</span>
              <span className="inline-flex items-center gap-0">
                <span className="text-xs font-medium text-muted-foreground">by</span>
                <img src={bgLogo} alt="Gowitek" className="h-5 w-auto object-contain" />
              </span>
            </div>
          </div>
          <span className="inline-flex items-center gap-2 px-6 py-2 rounded-full bg-[#0d1117] text-white text-base font-bold tracking-wide border border-white/10 whitespace-nowrap shadow-sm">
            Report at $0
          </span>
        </div>

        <div className="text-center mb-8">
          <h1 className="text-3xl font-semibold text-slate-900 tracking-tight">
            User Details
          </h1>
          <p className="mt-3 text-slate-600 text-sm leading-relaxed">
            Validate your email to receive your Signal AI readiness report.
          </p>
          <p className="mt-2 text-xs text-slate-400">
            A practical starting point before any major AI investment.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label
              htmlFor="fullName"
              className="block text-xs font-medium text-slate-700 mb-1.5"
            >
              Full Name <span className="text-sky-500">*</span>
            </label>
            <input
              id="fullName"
              name="name"
              type="text"
              value={form.name}
              onChange={handleChange}
              placeholder="Jane Cooper"
              className="w-full h-[50px] px-4 rounded-lg border border-slate-200 bg-white text-slate-900 placeholder:text-slate-400 text-sm outline-none transition focus:ring-2 focus:ring-sky-500 focus:border-sky-500"
              required
            />
          </div>

          <div>
            <label
              htmlFor="workEmail"
              className="block text-xs font-medium text-slate-700 mb-1.5"
            >
              Work Email <span className="text-sky-500">*</span>
            </label>
            <input
              id="workEmail"
              name="email"
              type="email"
              value={form.email}
              onChange={handleChange}
              placeholder="jane@company.com"
              className="w-full h-[50px] px-4 rounded-lg border border-slate-200 bg-white text-slate-900 placeholder:text-slate-400 text-sm outline-none transition focus:ring-2 focus:ring-sky-500 focus:border-sky-500"
              required
            />
            {form.email && !isValidEmail(form.email) && (
              <p className="mt-1.5 text-xs text-red-500">
                Please enter a valid email address.
              </p>
            )}
          </div>

          <button
            type="submit"
            disabled={!isValid}
            className={`w-full h-[52px] rounded-xl text-white text-sm font-semibold tracking-wide transition shadow-sm mt-2 ${
              isValid
                ? "bg-sky-500 hover:bg-sky-600 active:bg-sky-700 shadow-sky-500/20"
                : "bg-slate-300 cursor-not-allowed"
            }`}
          >
            Send OTP
          </button>
        </form>

        <div className="mt-6 text-center space-y-1">
          <p className="text-xs font-medium text-slate-700">
            {/* <span className="font-bold text-black">Start at $0.</span> No upfront commitment. */}
          </p>
          <p className="text-xs text-slate-400">
            No generic reports. Only actionable ROI insights.
          </p>
        </div>

      
        </div>{/* end form card */}
        </div>{/* end right panel */}
      </div>{/* end grid */}

      {popup && (
        <div className="fixed inset-0 z-10 flex items-center justify-center bg-black/30 px-4">
          <div className="w-full max-w-sm rounded-xl bg-white p-5 shadow-lg">
            <p className="text-sm text-slate-700">{popup}</p>
            <button
              type="button"
              onClick={() => setPopup("")}
              className="mt-4 h-10 w-full rounded-lg bg-sky-500 text-sm font-semibold text-white transition hover:bg-sky-600"
            >
              OK
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default SignalAIUserDetails;
